import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ArticleLayoutProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  breadcrumbs?: { label: string; to: string }[];
  lastUpdated?: string;
  author?: string;
}

export default function ArticleLayout({
  title,
  description,
  children,
  breadcrumbs,
  lastUpdated,
  author = 'CSSBuy Spreadsheet Resource Hub',
}: ArticleLayoutProps) {
  const { t } = useTranslation('home');

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <li key={crumb.to} className="flex items-center gap-2">
                  {index > 0 && <i className="ri-arrow-right-s-line text-foreground-400"></i>}
                  <Link
                    to={crumb.to}
                    className={`transition-colors ${
                      index === breadcrumbs.length - 1
                        ? 'text-foreground-700 font-medium'
                        : 'text-foreground-500 hover:text-primary-700'
                    }`}
                  >
                    {crumb.label}
                  </Link>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <header className="mb-8 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {title}
          </h1>
          {description && (
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {description}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            {author && (
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {author}
              </span>
            )}
            {lastUpdated && (
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Last Updated: {lastUpdated}
              </span>
            )}
          </div>
        </header>

        <div className="max-w-none text-foreground-700 leading-relaxed">
          <div className="[&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:font-heading
               [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:font-heading
               [&_p]:mb-4 [&_p]:leading-relaxed
               [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-2
               [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-2
               [&_li]:text-foreground-700
               [&_strong]:font-bold [&_strong]:text-foreground-900
               [&_a]:text-primary-700 [&_a]:no-underline hover:[&_a]:underline">
            {children}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 md:p-5">
            <p className="text-sm text-accent-800 font-medium flex items-start gap-2">
              <i className="ri-information-line text-lg mt-0.5"></i>
              <span>{t('independent_notice')}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}